import { useEffect, useRef, useState, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { BOTTOM_NAV_ITEMS, CONSTRUCTION_NAV_ITEMS, GESTION_EAU_NAV_ITEMS } from '../../constants';
import { useModuleAccess } from '../../modules/navy-ay/context/useModuleAccess';
import { NavySymbol } from '../../modules/navy-ay/components/NavyLogo';
import { useNavyRoles } from '../../modules/navy-ay/context/useNavyRoles';
import { Home, Wallet, ArrowUpDown, PieChart, Target, Users, LayoutDashboard, ShoppingCart, Package, Warehouse, PlusCircle, Gauge, TrendingUp, Network, FileText, Droplet, Receipt, Waves, GripVertical, Check, Store, QrCode, Truck, Inbox, Settings, UserPlus, Map as MapIcon, Navigation, Send, PackageOpen, BellRing, Route, Banknote, Mic, Zap } from 'lucide-react';
import { useModuleSwitcher, type Module } from '../../contexts/ModuleSwitcherContext';
import { ConstructionContext } from '../../modules/construction-poc/context';
import { canAccessBCI } from '../../modules/construction-poc/utils/rolePermissions';
import { GestionEauContext } from '../../modules/gestion-eau/context';
import { filterEauNavByRoles } from '../../modules/gestion-eau/components/eauNav';
import { useAppStore } from '../../stores/appStore';
import apiService from '../../services/apiService';
import { orderModules } from '../../utils/moduleOrder';
import { routeDictee } from '../../modules/dictee/dicteeParModule';
import { usePendingDictations } from '../../modules/dictee/usePendingDictations';
import BadgeDictees from '../../modules/dictee/components/BadgeDictees';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Icône d'un module dans le sélecteur : emoji, sauf NAVY ay (symbole du logo,
 * repli « N » sur pastille jaune si le fichier manque).
 */
/** PUDi (v3.93.0) : pastille aux 11 couleurs des secteurs (signature de la charte PIC). */
const PUDI_SECTOR_COLORS = ['#C2512D', '#0B93A1', '#2E8B4E', '#D0386A', '#6A4DC4', '#1C7DC4', '#D3940E', '#55678A', '#E16A22', '#A53A8D', '#2C3C82'];

const ModuleIcon = ({ module }: { module: Module }) =>
  module.id === 'validation' ? (
    // Validation client (VALIDCLIENT-CC-02) : coche dans un carré, bleu de la charte du module.
    <span className="block w-5 h-5 leading-none" role="img" aria-label={module.name}>
      <svg viewBox="0 0 20 20" className="w-5 h-5" aria-hidden="true">
        <rect x="1" y="1" width="18" height="18" rx="4" fill="#0369a1" />
        <path d="M5.5 10.5l3 3 6-7" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ) : module.id === 'pudi' ? (
    <span
      className="flex w-5 h-5 rounded-md overflow-hidden"
      role="img"
      aria-label={module.name}
    >
      {PUDI_SECTOR_COLORS.map((c) => (
        <span key={c} className="flex-1 h-full" style={{ background: c }} />
      ))}
    </span>
  ) : module.id === 'navy-ay' ? (
    <span className="block w-5 h-5 leading-none" role="img" aria-label={module.name}>
      <NavySymbol className="w-5 h-5 text-[11px]" rounded="rounded-md" />
    </span>
  ) : (
    <span className="text-xl leading-none" role="img" aria-label={module.name}>
      {module.icon}
    </span>
  );

/**
 * Élément triable d'un module en mode « réorganiser » (glisser-déposer @dnd-kit).
 * L'élément entier sert de poignée de prise (listeners) ; un repère GripVertical
 * et l'anneau sur le module actif rendent l'affordance lisible.
 */
const SortableModuleItem = ({ module, isActive }: { module: Module; isActive: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: module.id
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`mobile-nav-item flex-shrink-0 touch-none select-none cursor-grab active:cursor-grabbing ${isDragging ? 'z-10' : ''}`}
      aria-label={`Déplacer ${module.name}`}
    >
      <div className={`relative p-3 rounded-xl bg-slate-50 ${isActive ? 'ring-2 ring-blue-400' : ''}`}>
        <ModuleIcon module={module} />
        <GripVertical className="absolute -top-1 -right-1 w-3 h-3 text-slate-400" />
      </div>
      <span className="text-xs font-semibold mt-1 text-slate-600">{module.name}</span>
    </div>
  );
};

/*
 * SPACING CHANGES MADE FOR COMPACT BOTTOM NAV:
 * - Container padding: py-4 → py-2 → py-1.5 (16px → 8px → 6px vertical)
 * - Icon size: w-6 h-6 → w-5 h-5 → w-[18px] h-[18px] (24px → 20px → 18px)
 * - Text margin: mt-2 → mt-1 (8px → 4px) - preserved for readability
 * - Icon container padding: p-3 → p-2 → p-1.5 (12px → 8px → 6px) in CSS
 * - Estimated height reduction: 80-90px → 55-65px → 48-56px
 */

const iconMap = {
  // BazarKELY icons
  Home,
  Wallet,
  ArrowUpDown,
  PieChart,
  Target,
  Users,
  // Construction POC icons
  LayoutDashboard,
  ShoppingCart,
  Package,
  Warehouse,
  PlusCircle,
  // Gestion Eau (AHUVI) icons
  Gauge,
  TrendingUp,
  Network,
  FileText,
  Droplet,
  Receipt,
  Waves,
  // Gestion Eau, domaine Électricité (ELEC-CC-01)
  Zap,
  // NAVY ay icons (phase 1A)
  Store,
  QrCode,
  Truck,
  Inbox,
  Settings,
  UserPlus,
  // NAVY ay icons (phase 1B)
  Map: MapIcon,
  Navigation,
  // NAVY ay icons (phase 2A)
  Send,
  PackageOpen,
  BellRing,
  Route,
  Banknote,
  // Dictée (BARRE-CC-01)
  Mic
};

const BottomNav = () => {
  const {
    isSwitcherMode,
    activeModule,
    availableModules,
    setActiveModule,
    setSwitcherMode
  } = useModuleSwitcher();
  const navRef = useRef<HTMLElement>(null);

  // Utilisateur (Zustand) : source de l'ordre personnalisé des modules, persisté
  // localement et synchronisé via preferences.moduleOrder.
  const user = useAppStore((s) => s.user);
  const setUser = useAppStore((s) => s.setUser);
  // v3.80.0 : seuls les modules accessibles au compte (règles d'accès aux modules),
  // dans l'ordre moduleOrder existant. Filtre d'AFFICHAGE uniquement (pas de sécurité).
  const { accessible: accessibleModuleIds } = useModuleAccess();
  const orderedModules = orderModules(availableModules, user?.preferences?.moduleOrder).filter(
    (m) => accessibleModuleIds.includes(m.id)
  );

  // Sous-mode « réorganiser » (glisser-déposer) du switcher.
  const [isReorderMode, setIsReorderMode] = useState(false);

  // Détection d'appui long pour entrer en réorganisation (tap court = bascule).
  const longPressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 80, tolerance: 8 } })
  );

  // Toujours sortir de la réorganisation quand on quitte le mode switcher.
  useEffect(() => {
    if (!isSwitcherMode) setIsReorderMode(false);
  }, [isSwitcherMode]);

  // Repartir d'un drapeau d'appui long propre hors réorganisation : évite qu'un
  // appui long (qui a ouvert la réorganisation sans clic consommateur, le bouton
  // ayant été démonté) ne fasse avaler le tap suivant (souris ou clavier).
  useEffect(() => {
    if (!isReorderMode) longPressFired.current = false;
  }, [isReorderMode]);

  const cancelLongPress = () => {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleModulePointerDown = (e: React.PointerEvent) => {
    longPressFired.current = false;
    pointerStart.current = { x: e.clientX, y: e.clientY };
    cancelLongPress();
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      setIsReorderMode(true);
    }, 400);
  };

  const handleModulePointerMove = (e: React.PointerEvent) => {
    if (!pointerStart.current) return;
    const dx = Math.abs(e.clientX - pointerStart.current.x);
    const dy = Math.abs(e.clientY - pointerStart.current.y);
    if (dx > 10 || dy > 10) cancelLongPress();
  };

  /**
   * Persiste un nouvel ordre de modules : d'abord optimiste local (instantané,
   * hors-ligne, persisté par Zustand), puis synchro Supabase best-effort sans
   * jamais bloquer l'UI ni écraser les autres préférences.
   */
  const persistModuleOrder = async (moduleOrder: string[]) => {
    if (!user) return;
    const merged = { ...user.preferences, moduleOrder };
    setUser({ ...user, preferences: merged }); // optimiste local
    try {
      const result = await apiService.updateUserPreferences(user.id, merged);
      if (result.success && result.data) setUser(result.data as unknown as Parameters<typeof setUser>[0]);
    } catch {
      // Hors-ligne / réseau bloqué : l'ordre local reste appliqué, repartira au
      // prochain changement en ligne.
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedModules.map((m) => m.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    // Les modules masqués (non accessibles) gardent leur rang en fin de liste.
    const hidden = orderModules(availableModules, user?.preferences?.moduleOrder)
      .map((m) => m.id)
      .filter((id) => !ids.includes(id));
    persistModuleOrder([...arrayMove(ids, oldIndex, newIndex), ...hidden]);
  };
  
  // Get user role for BCI access control (AGENT 11)
  // Use useContext directly to safely check if ConstructionProvider exists
  const constructionContext = useContext(ConstructionContext);
  const userRole = constructionContext?.userRole || null;
  const showBCIItems = activeModule?.id === 'construction'
    ? canAccessBCI(userRole)
    : true; // Show all items if not in Construction module

  // Gestion Eau : rôles (admin/releveur/client, cumulables) pour filtrer la nav du module.
  // useContext direct → ne plante pas si le provider n'est pas monté.
  const eauContext = useContext(GestionEauContext);
  const eauRoles = eauContext?.roles ?? null;
  const isEauModule = activeModule?.id === 'gestion-eau';
  // Domaine actif du module eau (ELEC-CC-01) : la barre ne montre que ses boutons.
  const eauDomaine = eauContext?.domaineActif ?? 'eau';
  // Pastilles des boutons du module eau (ELEC-CC-03 : factures à valider pour un valideur).
  const eauNavBadges: Record<string, number> = eauContext?.navBadges ?? {};
  const isElecDomain = isEauModule && eauDomaine === 'elec';
  // NAVY ay : barre anthracite / jaune (charte NAVY, jamais de bleu marine).
  const isNavyModule = activeModule?.id === 'navy-ay';
  // NAVY ay (v3.81.0) : barre selon le rôle « Je suis » + pastille des demandes en attente.
  const navyRoles = useNavyRoles();
  // Dictée (BARRE-CC-01) : compteurs du badge posé sur le micro de la barre.
  const dictees = usePendingDictations();

  // Click-outside detection to exit switcher mode
  useEffect(() => {
    if (!isSwitcherMode) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // Check if click is outside the nav element
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        // Exit switcher mode (and reorder sub-mode) when clicking outside
        setIsReorderMode(false);
        setSwitcherMode(false);
      }
    };

    // Add event listener with a small delay to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isSwitcherMode, setSwitcherMode]);

  /**
   * Handle module switch
   */
  const handleModuleSwitch = (moduleId: string) => {
    // Si on est déjà dans ce module, fermer juste le mode switcher
    if (activeModule?.id === moduleId) {
      setSwitcherMode(false);
      return;
    }

    // Changer de module
    setActiveModule(moduleId);
    // setActiveModule ferme déjà le mode switcher automatiquement
  };

  /**
   * Render navigation mode (shows BazarKELY or Construction items based on active module)
   */
  const renderNavigationMode = () => {
    // Select navigation items based on active module.
    // Construction → CONSTRUCTION_NAV_ITEMS, Gestion Eau → GESTION_EAU_NAV_ITEMS (rôle-filtrés),
    // sinon BazarKELY.
    let navItems: ReadonlyArray<{ path: string; icon: string; label: string }> =
      activeModule?.id === 'construction'
        ? CONSTRUCTION_NAV_ITEMS
        : isNavyModule
        ? navyRoles.navItems
        : isEauModule
        ? eauRoles
          ? filterEauNavByRoles(eauRoles, GESTION_EAU_NAV_ITEMS, eauDomaine)
          : []
        : BOTTOM_NAV_ITEMS;

    // Filtre BCI (Construction)
    if (activeModule?.id === 'construction' && !showBCIItems) {
      // Filter out BCI-related navigation items if user doesn't have access
      navItems = navItems.filter(item => {
        // Hide "Commandes" (orders) and "Nouvelle commande" (new-order) for unauthorized roles
        return item.path !== '/construction/orders' && item.path !== '/construction/new-order';
      });
    }

    // Dictée (BARRE-CC-01) : micro de la barre budget, seulement si le module actif
    // figure dans la table des dictées (sinon aucun micro, rien de trompeur).
    const isBudgetBar = navItems === BOTTOM_NAV_ITEMS;
    const micRoute = isBudgetBar ? routeDictee(activeModule?.id ?? 'bazarkely') : null;
    if (isBudgetBar && !micRoute) navItems = navItems.filter((it) => it.path !== '/dicter');

    // Garde-fou : jamais plus de 6 boutons dans la barre (cumul de rôles rare).
    if (navItems.length > 6) navItems = navItems.slice(0, 6);
    // NAVY ay (3B) : barre de 6 boutons (opératrice) = libellés en 10,5 px et marges
    // resserrées, pour tenir sur UNE ligne dès 360 px (« Partenaires » = 53 px).
    const navyDense = isNavyModule && navItems.length >= 6;

    // Thème actif : vert AHUVI en mode eau, bleu sinon.
    // Domaine Électricité (ELEC-CC-01) : vert RTB.
    const activeBg = isElecDomain ? 'bg-rtb-green' : isEauModule ? 'bg-ahuvi-forest' : 'bg-blue-600';
    const hoverBg = isElecDomain ? 'hover:bg-rtb-gold/15' : isEauModule ? 'hover:bg-ahuvi-50' : 'hover:bg-blue-50';
    const activeText = isElecDomain ? 'text-rtb-green' : isEauModule ? 'text-ahuvi-forest' : 'text-blue-600';

    return (
      <nav 
        ref={navRef}
        className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-2xl z-50 safe-area-inset overscroll-none lg:hidden"
      >
        <div className={`flex items-center justify-around py-1.5 animate-in fade-in duration-300${isNavyModule ? ' px-1' : ''}`}>
          {navItems.map((item) => {
            const IconComponent = iconMap[item.icon as keyof typeof iconMap];
            // Routes "racine" (dashboard eau, espace client) → match exact pour ne pas
            // rester actives sur leurs sous-routes (ex. /gestion-eau/client/factures).
            const exact = item.path === '/gestion-eau' || item.path === '/gestion-eau/client' || item.path === '/gestion-eau/elec'
              || (isNavyModule && 'end' in item && !!item.end);
            // NAVY ay : pastille du nombre de demandes en attente (opératrice).
            const navyBadge = isNavyModule && navyRoles.badges[item.path]
              ? navyRoles.badges[item.path]
              : 0;

            // Dictée (BARRE-CC-01) : rond bleu à la taille des autres pictogrammes (42 px).
            // Le badge est un lien frère (jamais un lien dans un lien).
            if (micRoute && item.path === micRoute) {
              return (
                <div key={item.path} className="relative" data-testid="nav-mic">
                  <NavLink
                    to={micRoute}
                    aria-label="Dicter une dépense"
                    className="group flex flex-col items-center justify-center p-1.5 max-[380px]:px-0.5 rounded-lg focus:outline-none"
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={`flex h-[42px] w-[42px] items-center justify-center rounded-full text-white shadow-md transition-colors duration-200 group-focus-visible:ring-4 group-focus-visible:ring-blue-300 ${
                            isActive ? 'bg-blue-700' : 'bg-blue-600 group-hover:bg-blue-700'
                          }`}
                        >
                          <IconComponent className="w-[18px] h-[18px]" aria-hidden="true" />
                        </span>
                        <span className={`text-xs font-semibold mt-1 transition-colors duration-200 ${isActive ? 'text-blue-600' : 'text-slate-600'}`}>
                          {item.label}
                        </span>
                      </>
                    )}
                  </NavLink>
                  <BadgeDictees comptes={dictees} className="absolute -top-1 right-0" />
                </div>
              );
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={exact}
                className={({ isActive }) =>
                  isNavyModule
                    ? // NAVY ay (3B) : boutons de largeur égale, libellé sur UNE ligne de 360 à 430 px
                      `flex flex-1 min-w-0 justify-center ${navyDense ? 'px-0' : 'px-0.5'} focus:outline-none focus-visible:ring-2 focus-visible:ring-navyay-charcoal rounded-2xl`
                    : `mobile-nav-item ${isActive ? 'active' : ''}${isBudgetBar ? ' max-[380px]:px-0.5' : ''}`
                }
              >
                {({ isActive }) => isNavyModule ? (
                  // NAVY ay : le bouton actif ENTIER est anthracite, icône et libellé jaunes
                  // (jaune sur blanc = contraste insuffisant, donc jamais de libellé jaune hors pastille).
                  <span
                    className={`relative flex w-full max-w-[5.75rem] flex-col items-center rounded-2xl ${navyDense ? 'px-0.5' : 'px-1'} pt-2 pb-1.5 transition-colors duration-300 ${
                      isActive ? 'bg-navyay-charcoal shadow-lg' : 'hover:bg-navyay-yellow/15'
                    }`}
                  >
                    <IconComponent className={`w-[18px] h-[18px] ${isActive ? 'text-navyay-yellow' : 'text-navyay-charcoal'}`} />
                    {navyBadge > 0 && (
                      <span
                        className="absolute top-0.5 right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-navyay-yellow text-navyay-charcoal text-[11px] font-bold leading-[18px] text-center ring-2 ring-white tabular-nums"
                        aria-label={`${navyBadge} en attente`}
                      >
                        {navyBadge > 99 ? '99+' : navyBadge}
                      </span>
                    )}
                    <span className={`mt-1 block w-full text-center ${navyDense ? 'text-[10.5px]' : 'text-[11px]'} leading-tight font-semibold whitespace-nowrap ${isActive ? 'text-navyay-yellow' : 'text-navyay-charcoal'}`}>
                      {item.label}
                    </span>
                  </span>
                ) : (
                  <>
                    <div className={`relative p-3 rounded-xl transition-all duration-300 ${isActive ? `${activeBg} shadow-lg scale-110` : `${hoverBg} hover:scale-105`}`}>
                      <IconComponent className={`w-[18px] h-[18px] transition-colors duration-200 ${isActive ? 'text-white' : 'text-slate-600'}`} />
                      {/* Gestion Eau (ELEC-CC-03) : factures à valider sans avis, pour un valideur. */}
                      {isEauModule && (eauNavBadges[item.path] ?? 0) > 0 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rtb-gold text-rtb-green text-[11px] font-bold leading-[18px] text-center ring-2 ring-white tabular-nums"
                          aria-label={`${eauNavBadges[item.path]} à valider`}
                          data-eau-nav-badge={item.path}
                        >
                          {eauNavBadges[item.path]}
                        </span>
                      )}
                    </div>
                    <span className={`text-xs font-semibold mt-1 transition-colors duration-200 ${isActive ? activeText : 'text-slate-600'}`}>
                      {item.label}
                    </span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    );
  };

  /**
   * Render switcher mode (shows all available modules with active indicator)
   */
  const renderSwitcherMode = () => {
    // Mode « réorganiser » : tous les modules (actif inclus) en liste triable.
    if (isReorderMode) {
      const ids = orderedModules.map((m) => m.id);
      return (
        <nav
          ref={navRef}
          className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-2xl z-50 safe-area-inset overscroll-none lg:hidden"
        >
          <div className="flex flex-col animate-in fade-in duration-300">
            <div className="flex items-center justify-between gap-2 px-3 pt-1.5 pb-0.5">
              <span className="text-[11px] leading-tight text-slate-500">
                Glissez les modules pour les ranger. Touchez ailleurs pour terminer.
              </span>
              <button
                onClick={() => setIsReorderMode(false)}
                className="flex items-center gap-1 flex-shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                aria-label="Terminer la réorganisation"
              >
                <Check className="w-3.5 h-3.5" />
                Terminé
              </button>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
                <div className="flex flex-nowrap items-center gap-1 overflow-x-auto px-2 py-1.5">
                  {orderedModules.map((module) => (
                    <SortableModuleItem
                      key={module.id}
                      module={module}
                      isActive={module.id === activeModule?.id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </nav>
      );
    }

    // Mode switcher normal : alternatives (module actif masqué), tap = bascule,
    // appui long = entrée en réorganisation. Rangée défilante horizontalement.
    const nonActiveModules = orderedModules.filter((module) => module.id !== activeModule?.id);

    return (
      <nav
        ref={navRef}
        className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-2xl z-50 safe-area-inset overscroll-none lg:hidden"
      >
        <div className="flex flex-col animate-in fade-in duration-300">
          {nonActiveModules.length === 0 ? (
            // v3.81.0 : un seul module accessible → message au lieu d'une rangée vide.
            <p className="px-4 py-4 text-center text-sm font-medium text-slate-600" role="status">
              {activeModule?.name ?? 'Ce module'} est votre seul module pour l’instant
            </p>
          ) : (
          <div className="flex items-center justify-around flex-nowrap overflow-x-auto py-1.5">
            {nonActiveModules.map((module) => (
              <button
                key={module.id}
                onClick={() => {
                  if (longPressFired.current) {
                    longPressFired.current = false;
                    return; // l'appui long a déjà ouvert la réorganisation
                  }
                  handleModuleSwitch(module.id);
                }}
                onPointerDown={handleModulePointerDown}
                onPointerMove={handleModulePointerMove}
                onPointerUp={cancelLongPress}
                onPointerLeave={cancelLongPress}
                onPointerCancel={cancelLongPress}
                className="mobile-nav-item flex-shrink-0 select-none"
                aria-label={`Sélectionner ${module.name}`}
              >
                <div className="p-3 rounded-xl transition-all duration-300 hover:bg-blue-50 hover:scale-105">
                  <ModuleIcon module={module} />
                </div>
                <span className="text-xs font-semibold mt-1 transition-colors duration-200 text-slate-600">
                  {module.name}
                </span>
              </button>
            ))}
          </div>
          )}
          {nonActiveModules.length > 0 && (
            <div className="pb-0.5 text-center text-[10px] text-slate-400">
              Appui long pour réorganiser
            </div>
          )}
        </div>
      </nav>
    );
  };

  // Render based on current mode
  return isSwitcherMode ? renderSwitcherMode() : renderNavigationMode();
};

export default BottomNav;
